import { defaultGuid } from '@equifinance/shared';
import { useCallback, useState } from 'react';
import useApplicationApi from '../../api/useApplicationApi';
import useAccountApi, { WithdrawReasonType } from '../../api/useAccountApi';
import { Application, UnderwritingStatuses } from './Application';
import { useTransitionBackward } from './useTransionBackward';

export const defaultApplication: Omit<Application, 'errors'> = {
  id: defaultGuid,
  reference: '',
  applicantNames: [],
  securityAddressPostCode: '',
  underwritingStatus: UnderwritingStatuses.Underwriting,
  status: 'WithBroker',
  rejectionReason: '',
  withdrawalReason: '',
  otherWithdrawalDetail: '',
  brokerageId: '',
  brokerageName: '',
  isCurrentUnderwriter: false,
  underwriterAssignedToId: '',
  underwriterAssignedToName: '',
  submissionDate: '',
  slaAmberTrigger: '',
  slaRedTrigger: '',
  lastUpdated: '',
  totalTasksOutstanding: 0,
  tasks: [],
  totalChecklistOutstanding: 0,
  checklists: [],
};

export const useApplications = () => {
  const [applications, setApplications] = useState<Application[]>([]);

  const {
    getApplications, putUpdateUnderwriterOwner,
    putReopenApplication,
    getRejectionReasons,
    putRejectApplication,
    putWithdrawApplication,
  } = useApplicationApi();

  const {
    getWithdrawalReasons,
  } = useAccountApi();

  const fetchApplications = useCallback(async () => {
    const result = await getApplications();
    setApplications(result || []);
  }, []);

  const updateApplication = useCallback((id: string, data: Partial<Application>) => {
    setApplications((prev) => prev.map((item) => (item.id === id ? { ...item, ...data } : item)));
  }, []);

  const updateUnderwriterOwner = async (id: string, ownerId: string, ownerName: string | null) => {
    updateApplication(id, { underwriterAssignedToId: ownerId, underwriterAssignedToName: ownerName ?? '' });
    await putUpdateUnderwriterOwner(id, ownerId, ownerName);
  };

  const { makeTransitionBackward } = useTransitionBackward(updateApplication);

  return {
    applications,
    setApplicationsData: setApplications,
    fetchApplications,
    updateUnderwriterOwner,
    changeApplicationStatus: makeTransitionBackward,
    async reopenApplication(id: string) {
      const response = await putReopenApplication(id);
      updateApplication(id, response);
    },
    getRejectionReasons,
    getWithdrawalReasons,
    async rejectApplication(id: string, rejectReason: WithdrawReasonType) {
      const response = await putRejectApplication(id, rejectReason);
      updateApplication(id, { ...response });
      return response;
    },
    async withdrawApplication(id: string, withdrawReason: WithdrawReasonType) {
      const response = await putWithdrawApplication(id, withdrawReason);
      updateApplication(id, { ...response });
      return response;
    },
  };
};
